import { supabase } from '../supabaseClient';
import { Day, LegMode, Stop, StopType, Trip } from '../types';
import { bySortKey, keyBetween } from './sortKey';
import { ensureCover } from './covers';
import type { BookingLeg, BookingResult, TransitNight } from './booking';
import { LAYOVER_MAX_HOURS, gapHours, legDetail } from './booking';
import { sameTown } from './towns';

// The planning board's data layer.
//
// Conventions that come from the offline-sync migration (roamoir-mvp
// 20260828000036) and are honoured here even though the web app is online-only
// today, so its rows are already shaped for the sync core:
//   - nothing is hard-deleted; rows get a deleted_at tombstone
//   - every read filters deleted_at is null
//   - day ids are derived from (trip_id, date), so two clients adding the same
//     date mint the same id instead of a second row
//   - ordering is sort_key, never sort_order
//
// Vocabulary: a "candidate" is a place with no day yet. A "leg" is a stop of
// stop_type 'transit'. An "open" leg has no leg_mode — the traveller has
// deliberately left how they get there until later.

// ── Dates ───────────────────────────────────────────────────────────────────
//
// Every date here is a YYYY-MM-DD string. They are built and formatted at noon
// UTC so no browser time zone can push one onto a neighbouring day.

export function formatDay(date: string, opts: { year?: boolean; weekday?: boolean } = {}): string {
  const d = new Date(`${date}T12:00:00Z`);
  return d.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    ...(opts.weekday === false ? {} : { weekday: 'short' }),
    day: 'numeric',
    month: 'short',
    ...(opts.year ? { year: 'numeric' } : {}),
  });
}

export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Every date from start to end inclusive. */
export function dateRange(start: string, end: string): string[] {
  const out: string[] = [];
  // A guard, not a product limit: a typo'd year should not mint 3,000 days.
  for (let d = start, i = 0; d <= end && i < 120; d = addDays(d, 1), i++) out.push(d);
  return out;
}

/** "HH:MM:SS" or "HH:MM" → "HH:MM". */
export function shortTime(t: string | null): string | null {
  return t ? t.slice(0, 5) : null;
}

export const MODE_LABELS: Record<LegMode, string> = {
  train: 'Train',
  flight: 'Flight',
  bus: 'Bus',
  car: 'Car',
  ferry: 'Ferry',
  walk: 'Walk',
  other: 'Other',
};

// ── Deterministic day ids ───────────────────────────────────────────────────

/**
 * RFC 4122 v5 uuid of `name` in `namespace`. Day ids are uuidV5(date, trip_id):
 * the same trip and date always give the same id on every device, which is
 * what lets the days_trip_date_uniq index turn a duplicate into a no-op.
 */
async function uuidV5(name: string, namespace: string): Promise<string> {
  const ns = namespace.replace(/-/g, '');
  const nsBytes = new Uint8Array(ns.match(/../g)!.map((h) => parseInt(h, 16)));
  const nameBytes = new TextEncoder().encode(name);
  const buf = new Uint8Array(nsBytes.length + nameBytes.length);
  buf.set(nsBytes);
  buf.set(nameBytes, nsBytes.length);
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-1', buf)).slice(0, 16);
  hash[6] = (hash[6] & 0x0f) | 0x50;
  hash[8] = (hash[8] & 0x3f) | 0x80;
  const hex = Array.from(hash, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function dayIdFor(tripId: string, date: string): Promise<string> {
  return uuidV5(date, tripId);
}

// ── Reads ───────────────────────────────────────────────────────────────────

export interface Board {
  trip: Trip;
  days: Day[];
  /** Live stops, ordered by (sort_key, stop_id). */
  stops: Stop[];
}

export async function fetchBoard(tripId: string): Promise<{ data: Board | null; error: string | null }> {
  const [t, d, s] = await Promise.all([
    supabase.from('trips').select('*').eq('trip_id', tripId).is('deleted_at', null).maybeSingle(),
    supabase.from('days').select('*').eq('trip_id', tripId).is('deleted_at', null).order('date'),
    supabase.from('stops').select('*').eq('trip_id', tripId).is('deleted_at', null),
  ]);
  const error = t.error ?? d.error ?? s.error;
  if (error) return { data: null, error: error.message };
  if (!t.data) return { data: null, error: null };
  const days = (d.data ?? []) as Day[];
  const live = new Set(days.map((x) => x.day_id));
  // A suggestion onto a day that has since been retired points nowhere; treat
  // it as no suggestion, or the place would be hidden from every view.
  const stops = ((s.data ?? []) as Stop[])
    .map((x) => (x.suggested_day_id && !live.has(x.suggested_day_id) ? { ...x, suggested_day_id: null } : x))
    .sort(bySortKey);
  return { data: { trip: t.data as Trip, days, stops }, error: null };
}

// ── Trips ───────────────────────────────────────────────────────────────────

export type TripBasics = Pick<
  Trip,
  | 'title'
  | 'destination'
  | 'themes'
  | 'brief'
  | 'travelers'
  | 'start_date'
  | 'start_place'
  | 'start_mode'
  | 'start_detail'
  | 'start_time'
  | 'end_date'
  | 'end_place'
  | 'end_mode'
  | 'end_detail'
  | 'end_time'
  | 'dates_precision'
>;

/** "Be in Shenzhen on the 19th" — a night pinned before the rest is planned. */
export interface FixedPoint {
  town: string;
  date: string;
  /** Known when it came from a booking: the move into this town is already decided. */
  mode?: LegMode | null;
  detail?: string | null;
  from?: string | null;
  /** From a booking whose legs are created on their own: pin the night, add no leg. */
  booked?: boolean;
}

/** A booked stay: pins its nights to a town and names the lodging. */
export interface BookedStay {
  town: string;
  name: string | null;
  check_in: string;
  check_out: string;
}

/** What a booking adds beyond the form: every leg, and the nights spent travelling. */
export interface Journey {
  legs: BookingLeg[];
  transitNights: TransitNight[];
}

export async function createTrip(
  basics: TripBasics,
  fixed: FixedPoint[],
  stays: BookedStay[] = [],
  journey: Journey | null = null,
): Promise<{ tripId: string | null; error: string | null }> {
  const { data: session } = await supabase.auth.getSession();
  const uid = session.session?.user.id;
  if (!uid) return { tripId: null, error: 'Not signed in' };

  const tripId = crypto.randomUUID();
  const { error } = await supabase.from('trips').insert({ trip_id: tripId, user_id: uid, ...basics });
  if (error) return { tripId: null, error: error.message };

  // The cover photo arrives in the background; the trip is usable without it.
  if (basics.destination) void ensureCover(tripId);
  const synced = await syncDays(tripId, basics.start_date, basics.end_date);
  if (synced.error) return { tripId, error: synced.error };
  const pinned = await pinFixedPoints(tripId, basics, fixed);
  if (pinned.error) return { tripId, error: pinned.error };
  const stayed = await pinStays(tripId, basics, stays);
  if (stayed.error || !journey) return { tripId, error: stayed.error };
  const travelled = await addJourney(tripId, basics, journey);
  return { tripId, error: travelled.error };
}

/**
 * Every booked leg goes on the day it leaves, with both ends, so the board can
 * show the layovers between them. A night spent on board is fixed with no town.
 */
async function addJourney(tripId: string, basics: TripBasics, journey: Journey): Promise<{ error: string | null }> {
  if (!basics.start_date || !basics.end_date) return { error: null };
  const inRange = (d: string) => d >= basics.start_date! && d <= basics.end_date!;
  const legs: NewStop[] = [];
  for (const l of journey.legs.filter((x) => inRange(x.date))) {
    legs.push({
      title: `${l.from} → ${l.to}`,
      stop_type: 'transit',
      icon: legIcon(l.mode),
      city: l.to,
      day_id: await dayIdFor(tripId, l.date),
      leg_mode: l.mode,
      context: legDetail(l),
      leg_from: l.from,
      leg_to: l.to,
      depart_time: l.depart_time,
      arrive_date: l.arrive_date,
      arrive_time: l.arrive_time,
    });
  }
  const made = await createStops(tripId, legs);
  if (made.error) return made;
  for (const n of journey.transitNights.filter((x) => inRange(x.date))) {
    const { error } = await supabase
      .from('days')
      .update({ in_transit: true, base_fixed: true, base_city: null, lodging: n.lodging })
      .eq('day_id', await dayIdFor(tripId, n.date));
    if (error) return { error: error.message };
  }
  return { error: null };
}

/**
 * A booked stay is a decision: each night from check-in to the night before
 * check-out sleeps in that town, at that place, pinned.
 */
async function pinStays(tripId: string, basics: TripBasics, stays: BookedStay[]): Promise<{ error: string | null }> {
  if (!basics.start_date || !basics.end_date) return { error: null };
  for (const st of stays) {
    for (const date of dateRange(st.check_in, addDays(st.check_out, -1))) {
      if (date < basics.start_date || date > basics.end_date) continue;
      const { error } = await supabase
        .from('days')
        .update({ base_city: st.town, lodging: st.name, base_fixed: true })
        .eq('day_id', await dayIdFor(tripId, date));
      if (error) return { error: error.message };
    }
  }
  return { error: null };
}

/**
 * Each fixed point pins its night and gets an open leg into it: the town is
 * decided, how to get there is not. The leg comes from the previous fixed
 * point, or the start, and asks to be decided three days ahead.
 */
async function pinFixedPoints(
  tripId: string,
  basics: TripBasics,
  fixed: FixedPoint[],
): Promise<{ error: string | null }> {
  const inRange = fixed
    .filter((f) => f.town.trim() && basics.start_date && basics.end_date && f.date >= basics.start_date && f.date <= basics.end_date)
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  if (!inRange.length) return { error: null };

  let from = basics.start_place?.trim() || null;
  const legs: NewStop[] = [];
  for (const f of inRange) {
    const dayId = await dayIdFor(tripId, f.date);
    const { error } = await supabase.from('days').update({ base_city: f.town.trim(), base_fixed: true }).eq('day_id', dayId);
    if (error) return { error: error.message };
    if (f.date !== basics.start_date && !f.booked) {
      // From a booking the move is already decided; typed by hand it is open.
      legs.push({
        title: `${f.from?.trim() || from || 'Previous stop'} → ${f.town.trim()}`,
        stop_type: 'transit',
        icon: legIcon(f.mode ?? null),
        city: f.town.trim(),
        day_id: dayId,
        leg_mode: f.mode ?? null,
        decide_by: f.mode ? null : addDays(f.date, -3),
        context: f.detail ?? null,
      });
    }
    from = f.town.trim();
  }
  return createStops(tripId, legs).then((r) => ({ error: r.error }));
}

export async function updateTripBasics(
  tripId: string,
  basics: TripBasics,
): Promise<{ error: string | null; strandedDays: number }> {
  const { error } = await supabase.from('trips').update(basics).eq('trip_id', tripId);
  if (error) return { error: error.message, strandedDays: 0 };
  return syncDays(tripId, basics.start_date, basics.end_date);
}

/**
 * Make the trip's days match its dates: one day per date from start to
 * finish. Dates that fall outside are tombstoned only when nothing is placed
 * on them — a day holding places is kept, and counted in `strandedDays` so the
 * board can say so. Losing a planned day to a changed date would be
 * unforgivable.
 */
export async function syncDays(
  tripId: string,
  start: string | null,
  end: string | null,
): Promise<{ error: string | null; strandedDays: number }> {
  if (!start || !end || end < start) return { error: null, strandedDays: 0 };

  const wanted = new Set(dateRange(start, end));
  // Tombstones included: a date that was removed and comes back must revive its
  // old row, because its deterministic id is already taken.
  const [{ data: all, error }, { data: stops }] = await Promise.all([
    supabase.from('days').select('day_id, date, deleted_at').eq('trip_id', tripId),
    supabase.from('stops').select('day_id').eq('trip_id', tripId).is('deleted_at', null).not('day_id', 'is', null),
  ]);
  if (error) return { error: error.message, strandedDays: 0 };

  const rows = (all ?? []) as Pick<Day, 'day_id' | 'date' | 'deleted_at'>[];
  const occupied = new Set((stops ?? []).map((s) => s.day_id as string));
  const byDate = new Map<string, Pick<Day, 'day_id' | 'date' | 'deleted_at'>>();
  for (const r of rows) {
    const prev = byDate.get(r.date);
    if (!prev || (prev.deleted_at && !r.deleted_at)) byDate.set(r.date, r);
  }

  const toInsert: { day_id: string; trip_id: string; date: string }[] = [];
  const toRevive: string[] = [];
  for (const date of Array.from(wanted)) {
    const row = byDate.get(date);
    if (!row) toInsert.push({ day_id: await dayIdFor(tripId, date), trip_id: tripId, date });
    else if (row.deleted_at) toRevive.push(row.day_id);
  }

  const toRetire: string[] = [];
  let strandedDays = 0;
  for (const r of rows) {
    if (r.deleted_at || wanted.has(r.date)) continue;
    if (occupied.has(r.day_id)) strandedDays++;
    else toRetire.push(r.day_id);
  }

  const now = new Date().toISOString();
  const results = await Promise.all([
    toInsert.length ? supabase.from('days').insert(toInsert) : null,
    toRevive.length ? supabase.from('days').update({ deleted_at: null }).in('day_id', toRevive) : null,
    toRetire.length ? supabase.from('days').update({ deleted_at: now }).in('day_id', toRetire) : null,
    // A tombstone does not fire `on delete set null`, so clear suggestions
    // onto retired days here; the place goes back to being an open candidate.
    toRetire.length
      ? supabase.from('stops').update({ suggested_day_id: null }).in('suggested_day_id', toRetire)
      : null,
  ]);
  const failed = results.find((r) => r?.error);
  return { error: failed?.error?.message ?? null, strandedDays };
}

export async function patchTrip(
  tripId: string,
  patch: Partial<Pick<Trip, 'title' | 'start_mode' | 'start_detail' | 'start_time' | 'end_mode' | 'end_detail' | 'end_time' | 'route_hints' | 'brief' | 'plan_summary'>>,
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('trips').update(patch).eq('trip_id', tripId);
  return { error: error?.message ?? null };
}

// ── Days ────────────────────────────────────────────────────────────────────

export async function updateDay(
  dayId: string,
  patch: Partial<Pick<Day, 'base_city' | 'lodging' | 'base_fixed' | 'in_transit' | 'label'>>,
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('days').update(patch).eq('day_id', dayId);
  return { error: error?.message ?? null };
}

// ── Stops ───────────────────────────────────────────────────────────────────

export interface NewStop {
  title: string;
  city?: string | null;
  stop_type?: StopType | null;
  icon?: string | null;
  context?: string | null;
  notes?: string | null;
  must_see?: boolean;
  day_id?: string | null;
  suggested_day_id?: string | null;
  sort_key?: string | null;
  visit_hours?: number | null;
  leg_mode?: LegMode | null;
  decide_by?: string | null;
  leg_from?: string | null;
  leg_to?: string | null;
  depart_time?: string | null;
  arrive_date?: string | null;
  arrive_time?: string | null;
}

export async function createStops(tripId: string, items: NewStop[]): Promise<{ data: Stop[]; error: string | null }> {
  if (items.length === 0) return { data: [], error: null };
  const rows = items.map((i) => ({
    stop_id: crypto.randomUUID(),
    trip_id: tripId,
    day_id: i.day_id ?? null,
    suggested_day_id: i.suggested_day_id ?? null,
    title: i.title,
    city: i.city ?? null,
    stop_type: i.stop_type ?? null,
    icon: i.icon ?? null,
    context: i.context ?? null,
    notes: i.notes ?? null,
    must_see: i.must_see ?? false,
    visit_hours: i.visit_hours ?? null,
    leg_mode: i.leg_mode ?? null,
    decide_by: i.decide_by ?? null,
    leg_from: i.leg_from ?? null,
    leg_to: i.leg_to ?? null,
    depart_time: i.depart_time ?? null,
    arrive_date: i.arrive_date ?? null,
    arrive_time: i.arrive_time ?? null,
    // Omitted → the assign_stop_sort_key trigger appends to the end of the day.
    ...(i.sort_key ? { sort_key: i.sort_key } : {}),
  }));
  const { data, error } = await supabase.from('stops').insert(rows).select();
  if (error) return { data: [], error: error.message };
  return { data: ((data ?? []) as Stop[]).sort(bySortKey), error: null };
}

export type StopPatch = Partial<
  Pick<Stop, 'day_id' | 'sort_key' | 'must_see' | 'city' | 'title' | 'suggested_day_id' | 'plan_note' | 'leg_mode' | 'decide_by' | 'context'>
>;

export async function patchStop(stopId: string, patch: StopPatch): Promise<{ error: string | null }> {
  const { error } = await supabase.from('stops').update(patch).eq('stop_id', stopId);
  return { error: error?.message ?? null };
}

export async function removeStop(stopId: string): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('stops')
    .update({ deleted_at: new Date().toISOString() })
    .eq('stop_id', stopId);
  return { error: error?.message ?? null };
}

/**
 * The sort_key that puts a card into `column` (an ordered list of the stops
 * already there) just before `beforeId`, or at the end when it is null. The
 * moving card itself is ignored, so dropping a card where it already is
 * computes a key between its own neighbours.
 */
export function keyForDrop(column: Stop[], movingId: string, beforeId: string | null): string {
  const list = column.filter((s) => s.stop_id !== movingId);
  const idx = beforeId ? list.findIndex((s) => s.stop_id === beforeId) : -1;
  const at = idx === -1 ? list.length : idx;
  const prev = list[at - 1]?.sort_key ?? null;
  const next = list[at]?.sort_key ?? null;
  if (next === null) return keyAfter(prev);
  try {
    return keyBetween(prev, next);
  } catch {
    // Two neighbours with no room between them (or out of order). Rare, and the
    // end of the column is always a valid place to land.
    return keyAfter(list[list.length - 1]?.sort_key ?? null);
  }
}

const KEY_STRIDE = 4096;
const KEY_MAX = 999999999999;

/**
 * A key at the end of a list, in the server's own 12-digit form when it can
 * be. A bare midpoint like '5' would sort above every 12-digit key and push
 * the insert trigger onto its append-a-'5' path for good, growing keys by a
 * character per insert.
 */
export function keyAfter(prev: string | null): string {
  if (prev === null) return String(KEY_STRIDE).padStart(12, '0');
  if (/^\d{12}$/.test(prev) && Number(prev) + KEY_STRIDE <= KEY_MAX) {
    return String(Number(prev) + KEY_STRIDE).padStart(12, '0');
  }
  return keyBetween(prev, null);
}

/** Placing a stop on a day settles it: any suggestion or left-out note goes. */
export function placePatch(board: Board, stopId: string, dayId: string | null, beforeId: string | null): StopPatch {
  const column = board.stops.filter((s) => s.day_id === dayId);
  return { day_id: dayId, sort_key: keyForDrop(column, stopId, beforeId), suggested_day_id: null, plan_note: null };
}

// ── Capacity ────────────────────────────────────────────────────────────────

// Hours of sightseeing a day holds. Matches what plan-trip tells the model.
export const FULL_DAY_HOURS = 7;
export const END_DAY_HOURS = 4.5;
/** A candidate with no visit length still costs something. */
const UNKNOWN_VISIT_HOURS = 1;

/**
 * Hours free for places on a day. None on a day spent wholly travelling — the
 * day you leave home on a night flight, or the one lost over the Pacific. A
 * day with a journey in it holds what a start or finish day does.
 */
export function dayCapacity(board: Board, day: Day): number {
  return airborne(board, day) ? 0 : day.date === board.trip.start_date || day.date === board.trip.end_date || travels(board, day) ? END_DAY_HOURS : FULL_DAY_HOURS;
}

function travels(board: Board, day: Day): boolean {
  return board.stops.some((s) => s.stop_type === 'transit' && (s.day_id === day.day_id || s.arrive_date === day.date));
}

/** Nothing arrives today, and you slept (or set off) on board. */
export function airborne(board: Board, day: Day): boolean {
  if (!day.in_transit) return false;
  const prev = board.days.find((d) => d.date === addDays(day.date, -1));
  const arrives = board.stops.some((s) => s.stop_type === 'transit' && s.arrive_date === day.date);
  return !arrives && (day.date === board.trip.start_date || !!prev?.in_transit);
}

// ── The journey through a day ───────────────────────────────────────────────

export interface DayLayover {
  place: string;
  /** Local times; null when a leg has none. */
  from: string | null;
  until: string | null;
  hours: number | null;
  /** The leg that brings you in, and the one that takes you on. */
  inbound: Stop;
  outbound: Stop;
}

export type JourneyItem = { kind: 'leg'; stop: Stop } | { kind: 'layover'; layover: DayLayover };


/**
 * The day's legs in the order travelled, with a layover between two that meet
 * at the same place — including one that arrives today after leaving days ago.
 */
export function dayJourney(board: Board, day: Day): JourneyItem[] {
  const dateOf = new Map(board.days.map((d) => [d.day_id, d.date]));
  const legs = board.stops.filter((s) => s.stop_type === 'transit' && s.day_id);
  const leaving = legs
    .filter((s) => s.day_id === day.day_id)
    .sort((a, b) => ((a.depart_time ?? '99') < (b.depart_time ?? '99') ? -1 : 1));
  const items: JourneyItem[] = [];
  for (const out of leaving) {
    const inbound = legs.find((l) => {
      if (l === out || !sameTown(l.leg_to, out.leg_from)) return false;
      const arrive = l.arrive_date ?? dateOf.get(l.day_id!);
      if (!arrive || arrive > day.date) return false;
      if (arrive !== day.date && !(l.arrive_time && out.depart_time)) return false;
      return !(l.arrive_time && out.depart_time) || gapHours(arrive, l.arrive_time, day.date, out.depart_time) < LAYOVER_MAX_HOURS;
    });
    if (inbound) {
      const arrive = inbound.arrive_date ?? dateOf.get(inbound.day_id!)!;
      items.push({
        kind: 'layover',
        layover: {
          place: out.leg_from!,
          from: shortTime(inbound.arrive_time),
          until: shortTime(out.depart_time),
          hours: inbound.arrive_time && out.depart_time ? gapHours(arrive, inbound.arrive_time, day.date, out.depart_time) : null,
          inbound,
          outbound: out,
        },
      });
    }
    items.push({ kind: 'leg', stop: out });
  }
  return items;
}

/** Hours on a day: placed stops plus suggestions onto it. Legs don't count. */
export function dayLoad(board: Board, day: Day): number {
  return board.stops
    .filter((s) => s.stop_type !== 'transit' && (s.day_id === day.day_id || (!s.day_id && s.suggested_day_id === day.day_id)))
    .reduce((sum, s) => sum + (s.visit_hours ?? UNKNOWN_VISIT_HOURS), 0);
}

// ── The tree ────────────────────────────────────────────────────────────────

export interface TownGroup {
  town: string | null;
  /** Nights spent travelling: an overnight flight or train, no town. */
  transit: boolean;
  days: Day[];
  /** Nights in this town (the finish day, with no night, is not counted). */
  nights: number;
}

/** Consecutive days sleeping in one town, collapsed into one branch. */
export function townGroups(trip: Trip, days: Day[]): TownGroup[] {
  const out: TownGroup[] = [];
  for (const d of days) {
    const transit = !!d.in_transit && d.date !== trip.end_date;
    const town = transit ? null : d.base_city?.trim() || null;
    const last = out[out.length - 1];
    const night = d.date !== trip.end_date ? 1 : 0;
    // The finish day joins whatever town came before it: you wake up there.
    if (last && last.transit === transit && ((town === null ? last.town === null : sameTown(last.town, town)) || (!town && d.date === trip.end_date))) {
      last.days.push(d);
      last.nights += night;
    } else {
      out.push({ town, transit, days: [d], nights: night });
    }
  }
  return out;
}

// ── Decide later ────────────────────────────────────────────────────────────

export interface OpenChoice {
  key: string;
  label: string;
  by: string | null;
  /** Which thing to open when the chip is pressed. */
  target: { kind: 'start' } | { kind: 'end' } | { kind: 'leg'; stopId: string };
}

export function openChoices(board: Board): OpenChoice[] {
  const { trip, stops, days } = board;
  const out: OpenChoice[] = [];
  if (trip.start_date && !trip.start_mode) {
    out.push({ key: 'start', label: `How you get to ${trip.start_place ?? 'the start'}`, by: addDays(trip.start_date, -7), target: { kind: 'start' } });
  }
  const dayDate = new Map(days.map((d) => [d.day_id, d.date]));
  for (const s of stops) {
    if (s.stop_type === 'transit' && s.day_id && !s.leg_mode) {
      out.push({
        key: s.stop_id,
        label: s.title,
        by: s.decide_by ?? (dayDate.get(s.day_id) ? addDays(dayDate.get(s.day_id)!, -3) : null),
        target: { kind: 'leg', stopId: s.stop_id },
      });
    }
  }
  if (trip.end_date && !trip.end_mode) {
    out.push({ key: 'end', label: `How you leave ${trip.end_place ?? ''}`.trim(), by: addDays(trip.end_date, -3), target: { kind: 'end' } });
  }
  return out.sort((a, b) => ((a.by ?? '9') < (b.by ?? '9') ? -1 : 1));
}

// ── AI assistance (plan-trip edge function) ─────────────────────────────────

interface AiPlace {
  title: string;
  city: string | null;
  stop_type: StopType;
  icon: string;
  hours: number | null;
  must_see: boolean;
}

export interface ResearchIdea extends AiPlace {
  context: string | null;
  why: string | null;
  booking: string | null;
}

export interface ResearchResult {
  ideas: ResearchIdea[];
  notes: string | null;
}

export interface ParsedCandidate extends AiPlace {
  note: string | null;
  day_number: number | null;
}

/** A pasted list's own day: its heading, and the town slept in that night. */
export interface ParsedDay {
  day_number: number;
  label: string | null;
  sleep: string | null;
}

export interface ParseResult {
  candidates: ParsedCandidate[];
  has_day_order: boolean;
  days: ParsedDay[];
  travel_hints: string[];
  notes: string[];
}

export interface PlanResult {
  summary: string | null;
  nights: { date: string; base_city: string; why: string | null }[];
  legs: { date: string; from: string; to: string; options: string | null; recommended_mode: LegMode | null }[];
  suggestions: { stop_id: string; date: string }[];
  left_out: { stop_id: string; reason: string | null }[];
}

async function invokePlan<T>(body: Record<string, unknown>): Promise<{ data: T | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke('plan-trip', { body });
  if (error) {
    // FunctionsHttpError carries the function's own { error } body; surface it
    // rather than the generic "non-2xx status code" wrapper message.
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const payload = await ctx.json();
        if (payload?.error) return { data: null, error: String(payload.error) };
      } catch {
        // fall through to the wrapper message
      }
    }
    return { data: null, error: error.message };
  }
  return { data: data as T, error: null };
}

export function researchTrip(tripId: string, focus: string) {
  return invokePlan<ResearchResult>({ mode: 'research', trip_id: tripId, focus: focus || undefined });
}

export function parseList(tripId: string, text: string) {
  return invokePlan<ParseResult>({ mode: 'parse_list', trip_id: tripId, text });
}

/** Read a booking (a screenshot or its text) into the outline of a new trip. */
export function parseBooking(input: { text?: string; image?: { base64: string; mediaType: string } }) {
  return invokePlan<BookingResult>({
    mode: 'parse_booking',
    text: input.text || undefined,
    image_base64: input.image?.base64,
    media_type: input.image?.mediaType,
  });
}

export function planDays(tripId: string) {
  return invokePlan<PlanResult>({ mode: 'plan', trip_id: tripId });
}

/** Research idea → the candidate that gets saved. Generated prose goes in `context`. */
export function ideaToStop(idea: ResearchIdea): NewStop {
  const context = [
    idea.context,
    idea.why ? `Why: ${idea.why}` : null,
    idea.booking ? `Book ahead: ${idea.booking}` : null,
  ]
    .filter(Boolean)
    .join('\n\n');
  return {
    title: idea.title,
    city: idea.city,
    stop_type: idea.stop_type,
    icon: idea.icon,
    context: context || null,
    must_see: idea.must_see,
    visit_hours: idea.hours,
  };
}

/**
 * Add the reviewed candidates from a pasted list. With `useDayOrder` the
 * list's own days become *suggestions*, kept or dropped like any other — a
 * pasted plan is a first draft, never a decision. Each day's heading ("Luyi
 * (鹿邑): Taiqing Palace") names the day and its town fills the night, but only
 * where the traveller has not already named or decided them. Travel hints are kept on the
 * trip for planning; loose notes join the traveller's notes.
 */
export async function addParsed(
  board: Board,
  picked: ParsedCandidate[],
  opts: { useDayOrder: boolean; days?: ParsedDay[]; hints: string[]; notes: string[] },
): Promise<{ data: Stop[]; error: string | null }> {
  const dayByDate = new Map(board.days.map((d) => [d.date, d]));
  const items: NewStop[] = picked.map((c) => ({
    title: c.title,
    city: c.city,
    stop_type: c.stop_type,
    icon: c.icon,
    must_see: c.must_see,
    visit_hours: c.hours,
    // The traveller's own remark about the place, so it is notes, not context.
    notes: c.note,
    // By date, not array position: a kept day outside the dates must not shift the list.
    suggested_day_id:
      opts.useDayOrder && c.day_number && board.trip.start_date
        ? dayByDate.get(addDays(board.trip.start_date, c.day_number - 1))?.day_id ?? null
        : null,
  }));
  const created = await createStops(board.trip.trip_id, items);
  if (created.error) return created;

  if (opts.useDayOrder && board.trip.start_date) {
    for (const pd of opts.days ?? []) {
      const day = dayByDate.get(addDays(board.trip.start_date, pd.day_number - 1));
      if (!day) continue;
      const patch: Partial<Pick<Day, 'label' | 'base_city'>> = {};
      if (pd.label && !day.label) patch.label = pd.label;
      const openNight = !day.base_city && !day.base_fixed && !day.in_transit && day.date !== board.trip.end_date;
      if (pd.sleep && openNight) patch.base_city = pd.sleep;
      if (!Object.keys(patch).length) continue;
      const { error } = await updateDay(day.day_id, patch);
      if (error) return { data: created.data, error };
    }
  }

  const patch: Partial<Pick<Trip, 'route_hints' | 'brief'>> = {};
  if (opts.hints.length) {
    patch.route_hints = [board.trip.route_hints, opts.hints.join('; ')].filter(Boolean).join('; ');
  }
  if (opts.notes.length) {
    patch.brief = [board.trip.brief, `From my list: ${opts.notes.join('; ')}`].filter(Boolean).join('\n');
  }
  if (Object.keys(patch).length) {
    const { error } = await patchTrip(board.trip.trip_id, patch);
    if (error) return { data: created.data, error };
  }
  return created;
}

const LEG_ICONS: Record<string, string> = {
  train: '🚂',
  bus: '🚌',
  car: '🚗',
  ferry: '⛴️',
  flight: '✈️',
  walk: '🚶',
};

export function legIcon(mode: LegMode | null): string {
  return mode ? LEG_ICONS[mode] ?? '➡️' : '➡️';
}

/**
 * Apply a plan. Nothing the traveller decided is overwritten:
 *   - only open nights (no town, not fixed) get a town
 *   - a move gets a leg only if that day has none; the leg stays OPEN, with the
 *     options in its context — choosing the train is the traveller's call
 *   - candidates get a suggested day or a reason, never a day; the previous
 *     plan's suggestions and reasons are replaced
 */
export async function applyPlan(board: Board, plan: PlanResult): Promise<{ error: string | null }> {
  const dayByDate = new Map(board.days.map((d) => [d.date, d]));
  const ops: PromiseLike<{ error: { message: string } | null }>[] = [];

  // Every write is also guarded in the query itself: planning takes up to a
  // minute, and anything the traveller decided meanwhile must survive even if
  // `board` is a little stale.
  for (const n of plan.nights) {
    const day = dayByDate.get(n.date);
    if (day && !day.base_city && !day.base_fixed && n.date !== board.trip.end_date) {
      ops.push(
        supabase.from('days').update({ base_city: n.base_city }).eq('day_id', day.day_id).is('base_city', null).eq('base_fixed', false),
      );
    }
  }

  const suggested = new Map(plan.suggestions.map((s) => [s.stop_id, dayByDate.get(s.date)?.day_id ?? null]));
  const reasons = new Map(plan.left_out.map((l) => [l.stop_id, l.reason]));
  for (const s of board.stops) {
    if (s.day_id || s.stop_type === 'transit') continue;
    const next = { suggested_day_id: suggested.get(s.stop_id) ?? null, plan_note: reasons.get(s.stop_id) ?? null };
    if (next.suggested_day_id !== s.suggested_day_id || next.plan_note !== s.plan_note) {
      ops.push(supabase.from('stops').update(next).eq('stop_id', s.stop_id).is('day_id', null));
    }
  }

  const legs: NewStop[] = [];
  const legDays = new Set<string>();
  for (const l of plan.legs) {
    const day = dayByDate.get(l.date);
    if (!day || legDays.has(day.day_id)) continue;
    const column = board.stops.filter((s) => s.day_id === day.day_id);
    if (column.some((s) => s.stop_type === 'transit')) continue;
    legDays.add(day.day_id);
    const first = column[0]?.sort_key ?? null;
    legs.push({
      title: `${l.from} → ${l.to}`,
      stop_type: 'transit',
      icon: '➡️',
      city: l.to,
      day_id: day.day_id,
      // The move happens first, so the leg sorts to the top of its day.
      sort_key: first ? keyBetween(null, first) : null,
      decide_by: addDays(l.date, -3),
      context: [l.options, l.recommended_mode ? `Suggested: ${MODE_LABELS[l.recommended_mode]}.` : null].filter(Boolean).join('\n\n') || null,
    });
  }

  ops.push(supabase.from('trips').update({ plan_summary: plan.summary }).eq('trip_id', board.trip.trip_id));

  const results = await Promise.all(ops);
  const failed = results.find((r) => r.error);
  if (failed?.error) return { error: failed.error.message };
  const created = await createStops(board.trip.trip_id, legs);
  return { error: created.error };
}

/** Every suggestion becomes a placement, in board order. */
export function keepAllPatches(board: Board): { stopId: string; patch: StopPatch }[] {
  const working: Board = { ...board, stops: [...board.stops] };
  const out: { stopId: string; patch: StopPatch }[] = [];
  for (const s of board.stops) {
    if (s.day_id || !s.suggested_day_id) continue;
    const patch = placePatch(working, s.stop_id, s.suggested_day_id, null);
    working.stops = working.stops.map((x) => (x.stop_id === s.stop_id ? { ...x, ...patch } : x)).sort(bySortKey);
    out.push({ stopId: s.stop_id, patch });
  }
  return out;
}
